import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as shallowEqual from 'shallowequal';
import { CenteredSpinner } from '../CenteredSpinner';
import { DataGridSpinnerGlass } from '../style/DataGridStyles';
import { SuperimposedDivs } from '../style/LayoutComponents';
import {
  TypedRow,
  TypedRowHandler,
  TypedRowHandlerWithErrors,
  TypedRowStore,
  TypedRowStoreWithErrors,
  TypedRowStoreWithErrorsProps
} from './TypedDataGridInterfaces';

export type DataGridWithSynchronousRowStoreProps<TRowData, TError> =
  DataGrid.Props &
  TypedRowStoreWithErrorsProps<TRowData, TError> &
  DataGridWithSynchronousRowStoreOwnProps<TRowData, TError>;

export type DataGridWithSynchronousRowStoreOwnProps<TRowData, TError> = {
  showSpinnerWhileLoading: boolean;
  onError: TypedRowHandlerWithErrors<TRowData, TError>;
};

// Get a type-safe list of own prop keys so we can reliably strip them all out
type OwnPropsKeys = keyof DataGridWithSynchronousRowStoreOwnProps<unknown, unknown>;
const ownPropsNamesObj: Record<OwnPropsKeys, null> = {
  showSpinnerWhileLoading: null,
  onError: null
};

export type DataGridWithSynchronousRowStoreState<TRowData> = {
  readonly loadingFromNewRowStore: boolean;
  readonly currentlyStoredRows: Readonly<TypedRow<TRowData>>[];
};

/**
 * This component transforms the asynchronous callback-based API of the Data Grid's RowStore into a
 * "synchronous" pattern that more closely aligns with React idioms, storing the returned rows in
 * component state and re-rendering the data grid every time the stored rows are updated.
 *
 * This was originally built as a workaround for a race condition defect in the data grid (TFS 894890):
 * The data grid doesn't render measured column widths correctly with an async row store, so we
 * avoid that faulty code path by giving the data grid a row store that always returns rows synchronously.
 *
 * HOWEVER, this component now contains more functionality than simply the adapter for the RowStore.
 * It now also keeps track of loading and error states from the row store query.
 */
export class DataGridWithSynchronousRowStore<TRowData, TError> extends
  React.Component<DataGridWithSynchronousRowStoreProps<TRowData, TError>,
  DataGridWithSynchronousRowStoreState<TRowData>> {

  public readonly state: DataGridWithSynchronousRowStoreState<TRowData> = {
    loadingFromNewRowStore: true,
    currentlyStoredRows: [],
  };

  private previouslyReportedErrors: ReadonlyArray<TError> = [];

  private reportNewErrors(newRows: Readonly<TypedRow<TRowData>>[], newErrors: ReadonlyArray<TError>) {
    this.previouslyReportedErrors = newErrors;
    this.props.onError(newRows, newErrors);
  }

  // Keep track of the component being mounted, to avoid calling setState after unmount
  // internalIsMounted must start out as true so that the the first rowHandler callback will update
  // the state, if the backing row store happens to be synchronous.
  private internalIsMounted = true;
  public componentDidMount = () => this.internalIsMounted = true;
  public componentWillUnmount = () => this.internalIsMounted = false;

  // This is the getItems function that is passed as the "row store" to the actual datagrid
  private rowStoreGetItems = (start: number, end: number, rowHandler: TypedRowHandler<TRowData>): void => {
    this.fetchRowsAsynchronously(start, end);
    rowHandler(this.state.currentlyStoredRows);
  }

  private fetchRowsAsynchronously = (start: number, end: number): void => {
    const rowStore: TypedRowStoreWithErrors<TRowData, TError> = this.props.rowStore;
    rowStore.getItems(start, end, (newRows, newErrors) => {
      if (!this.internalIsMounted || rowStore !== this.props.rowStore) {
        return;
      }

      // Notify parent of any errors that came back. We do this here, rather than in componentDidUpdate,
      // because the latter causes the data grid not to apply the column widths.
      const hasNewErrors = newErrors && newErrors.length > 0 && !shallowEqual(newErrors, this.previouslyReportedErrors);
      if (hasNewErrors) {
        this.reportNewErrors(newRows, newErrors);

        // Notifying the parent component may cause this component to become unmounted
        if (!this.internalIsMounted) {
          return;
        }
      }

      this.setState(oldState => {
        // If we don't check for equality, we'll get stuck in an infinite loop of re-rendering the same rows.
        // Since our PaginatedRowStore shallow-copies the data every time it returns the row array, we have to use
        // a shallowEqual check, rather than simply reference equality.
        const rowsHaveChanged = !shallowEqual(oldState.currentlyStoredRows, newRows);

        if (rowsHaveChanged || hasNewErrors) {
          return {
            loadingFromNewRowStore: false,
            currentlyStoredRows: rowsHaveChanged ? newRows : oldState.currentlyStoredRows,
          };
        } else {
          return null;
        }
      });
    });
  }

  // Create a new row store only when the rows change. Creating a new row store will force the data grid to re-fetch,
  // so we want to do this only when there is actually new data to re-fetch (e.g. when the "async" fetch has completed)
  // but not more frequently (e.g. when the component gets re-rendered from above with no new props)
  private memoizedCreateRowStore: (rowStore: unknown, rows: unknown) => TypedRowStore<TRowData> = defaultMemoize((_rows: unknown) => {
    return { getItems: this.rowStoreGetItems };
  });

  public UNSAFE_componentWillReceiveProps(nextProps: DataGridWithSynchronousRowStoreProps<TRowData, TError>) {
    // When the row store from above has changed, we keep the old rows, but set the loading boolean to true.
    // This will cause the component to render a spinner but keep the old rows visible until the new rows arrive.
    if (this.props.rowStore !== nextProps.rowStore) {
      this.setState({
        loadingFromNewRowStore: true,
      });

      // Clear out the stored errors in ExplorerStyledDisplayGrid so they will be un-rendered
      // and new errors from the new row store can take their place. This is necessary because
      // there are two sources of state for errors: the rendered ErrorReporter objects and the error list
      // inside ErrorListProvider. In order to remove the errors from the ErrorListProvider, we unmount
      // the errors that are rendered by ExplorerStyledDisplayGrid, thus unregistering them from the error list.
      this.props.onError([], []);
    }
  }

  private getDataGridPropsOnly(): DataGrid.Props {
    const allProps = { ...this.props };
    for (const key in ownPropsNamesObj) {
      if (key in allProps) {
        delete (allProps as Record<string, unknown>)[key];
      }
    }
    return allProps;
  }

  public render(): React.ReactElement<DataGridWithSynchronousRowStoreProps<TRowData, TError>> {

    const dataGridPropsWithOverrides: DataGrid.Props = {
      ...this.getDataGridPropsOnly(),
      rowStore: this.memoizedCreateRowStore(this.props.rowStore, this.state.currentlyStoredRows),
      config: {
        ...this.props.config,
        rememberLastScrollPos: true, // Necessary so scroll doesn't jump to the top when the row store is replaced
      }
    };

    const displayLoadingSpinner = this.props.showSpinnerWhileLoading && this.state.loadingFromNewRowStore;

    return (
      <SuperimposedDivs>
        <DataGrid {...dataGridPropsWithOverrides} key='DataGrid' />
        {displayLoadingSpinner ? <OverlayWithSpinner key='Spinner' /> : null}
      </SuperimposedDivs>
    );
  }
}

export function OverlayWithSpinner(): JSX.Element | null {
  return (
    <>
      <DataGridSpinnerGlass />
      <div style={{ paddingTop: 48 }}>
        <CenteredSpinner />
      </div>
    </>
  );
}
